import { supabase, db } from '@/lib/supabase'
import { appFetch, isAppApiConfigured } from '@/lib/appApi'
import { isReservedTenantLabel, isValidTenantSubdomainLabel } from '@/lib/tenant/getSubdomain'
import { normalizeStoreSubdomainInput } from '@/lib/tenant/storeSubdomain'

// Types
export interface Store {
    id: string
    user_id: string
    name: string
    slug: string
    description: string | null
    template_id: number
    layout_config: any // Using 'any' for now to avoid circular dependency with TemplateConfig, or import it if possible
    logo_url: string | null
    custom_colors: Record<string, string>
    custom_fonts: Record<string, string>
    custom_css: string | null
    custom_domain: string | null
    subdomain: string | null
    meta_title: string | null
    meta_description: string | null
    meta_image: string | null
    facebook_url: string | null
    instagram_url: string | null
    tiktok_url: string | null
    contact_email: string | null
    contact_phone: string | null
    contact_address: string | null
    is_published: boolean
    is_maintenance_mode: boolean
    total_visits: number
    // Payment config moved to store_payment_config table
    // WhatsApp config moved to store_whatsapp_config table
    // Legacy JSONB kept temporarily for banner check (will be null after full migration)
    whatsapp_settings?: {
        enabled: boolean
        phone_number: string | null
        default_message: string
        show_on_products: boolean
        show_on_checkout: boolean
        show_floating_button: boolean
        quiet_hours_start: number
        quiet_hours_end: number
    }
    /** Public-safe keys only; secrets live in `store_secrets` (server). */
    integration_settings: {
        facebook_pixel_id?: string | number | null
        tiktok_pixel_id?: string | null
        /** Expéditeur (UI livraison) — non utilisé par les RPC de colis */
        shipping_sender_name?: string | null
        shipping_tax_id?: string | null
        /** Paramètres → Centre de communication. `true` uniquement = assistant affiché. */
        storefront_ai_chat_enabled?: boolean
    }
    created_at: string
    updated_at: string
    published_at: string | null
    /** Store-tier team seat cap (see `store_team_member_limit`); optional until migration applied */
    plan?: 'starter' | 'pro' | 'business' | string | null
    /** VIP / winback thresholds for email marketing (see `email_marketing_settings` migration). */
    email_marketing_settings?: {
        vip_min_orders?: number
        vip_min_spent?: number
        winback_inactive_days?: number
    } | null
    /**
     * Public storefront: payment method ids safe to display (cod, d17, flouci).
     * Empty array = hide footer payment badges. Synced from payment settings on save.
     */
    public_payment_methods?: string[] | null
}

/** `template_id` omis à la création → défaut serveur / valeur 1 appliquée dans `create()` (compat. DB). */
export type StoreInsert = Pick<Store, 'name' | 'slug'> &
  Partial<Pick<Store, 'template_id'>> &
  Partial<Omit<Store, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'name' | 'slug' | 'template_id'>>
export type StoreUpdate = Partial<Omit<Store, 'id' | 'user_id' | 'created_at' | 'updated_at'>>

// Stores Service
export const storesService = {
    // Get current user's store
    async getMyStore(): Promise<Store | null> {
        const list = await this.listAll()
        return list[0] ?? null
    },

    // List all user's stores
    async listAll(): Promise<Store[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; stores: Store[] }>('/api/app/stores')
            if (!j || typeof j !== 'object' || !Array.isArray((j as { stores?: unknown }).stores)) {
                throw new Error('Invalid API response')
            }
            const all = (j as { stores: Store[] }).stores
            // Match previous merge: owned rows first (created_at asc), then non-owned member stores (asc)
            const uid = user.id
            const ownerRows = all
                .filter((s) => s.user_id === uid)
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            const memberRows = all
                .filter((s) => s.user_id !== uid)
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            const byId = new Map<string, Store>()
            for (const s of [...ownerRows, ...memberRows]) {
                byId.set(s.id, s)
            }
            return Array.from(byId.values())
        }

        const { data: owned, error: ownedErr } = await db
            .from('stores')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true })

        if (ownedErr) throw ownedErr

        const { data: memberships, error: memErr } = await db
            .from('store_members')
            .select('store_id')
            .eq('user_id', user.id)

        if (memErr) throw memErr

        const memberIds = [...new Set((memberships ?? []).map(m => m.store_id).filter(Boolean) as string[])]
        let memberStores: Store[] = []
        if (memberIds.length > 0) {
            const { data: ms, error: msErr } = await db
                .from('stores')
                .select('*')
                .in('id', memberIds)
            if (msErr) throw msErr
            memberStores = (ms ?? []) as Store[]
        }

        const byId = new Map<string, Store>()
        for (const s of [...(owned ?? []), ...memberStores] as Store[]) {
            byId.set(s.id, s)
        }
        return Array.from(byId.values())
    },

    // Get store by ID
    async getById(id: string): Promise<Store | null> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; store: Store | null }>(
                `/api/app/stores/${encodeURIComponent(id)}`
            )
            if (!j || typeof j !== 'object' || !('store' in j)) {
                throw new Error('Invalid API response')
            }
            return (j as { store: Store | null }).store
        }

        const { data, error } = await db
            .from('stores')
            .select('*')
            .eq('id', id)
            .single()

        if (error) {
            if (error.code === 'PGRST116') return null
            throw error
        }
        return data
    },

    // In-memory cache for public stores (TTL: 60s)
    _storeCache: new Map<string, { data: Store, timestamp: number }>(),

    /**
     * Drop in-memory public store cache after saves. Pass slug and/or subdomain so both
     * tenant URL labels and canonical slugs refresh (they may differ).
     */
    clearPublicStoreCache(slug?: string | null, subdomain?: string | null) {
        if (slug) this._storeCache.delete(String(slug).trim().toLowerCase());
        if (subdomain) this._storeCache.delete(String(subdomain).trim().toLowerCase());
        if (!slug && !subdomain) this._storeCache.clear();
    },

    /**
     * Resolve a public storefront row by tenant hostname label (subdomain) or canonical slug.
     * Wildcard hosts use `stores.subdomain`; `/ref/:slug` uses `stores.slug` — they often differ.
     */
    async getBySlug(
        slug: string,
        options?: { bypassCache?: boolean }
    ): Promise<Store | null> {
        const bypassCache = options?.bypassCache === true;
        const label = String(slug ?? '').trim();
        if (!label) return null;
        const cacheKey = label.toLowerCase();

        if (!bypassCache) {
            const CACHE_TTL = 60 * 1000;
            const cached = this._storeCache.get(cacheKey);

            if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
                console.log(`[StoreCache] HIT for ${cacheKey}`);
                return cached.data;
            }
        }

        const debugTenant =
            import.meta.env.VITE_DEBUG_TENANT === 'true' || import.meta.env.VITE_DEBUG_TENANT === '1';

        // 0) Full hostname saved as custom_domain (rare; storefront usually resolves via resolveCustomDomainSlug)
        let data: Store | null = null;
        if (label.includes('.')) {
            const domainKey = label.replace(/^www\./i, '').toLowerCase();
            const r = await db
                .from('stores')
                .select('*')
                .eq('custom_domain', domainKey)
                .eq('custom_domain_status', 'active')
                .maybeSingle();
            if (r.error) throw r.error;
            data = r.data as Store | null;
        }

        // 1) Subdomain (wildcard: sebriboutique.usefastsell.com → subdomain ilike match, case-insensitive)
        if (!data) {
            const r1 = await db
                .from('stores')
                .select('*')
                .ilike('subdomain', label)
                .limit(1)
                .maybeSingle();
            if (r1.error) throw r1.error;
            data = r1.data as Store | null;
        }

        // 2) Canonical slug (reference URLs, legacy)
        if (!data) {
            const r2 = await db
                .from('stores')
                .select('*')
                .eq('slug', label)
                .maybeSingle();
            if (r2.error) throw r2.error;
            data = r2.data as Store | null;
        }

        if (debugTenant) {
            console.info('[tenant:store]', {
                label: cacheKey,
                found: Boolean(data),
                id: (data as Store | null)?.id,
                slug: (data as Store | null)?.slug,
                subdomain: (data as Store | null)?.subdomain,
            });
        }

        if (data) {
            this._storeCache.set(cacheKey, { data: data as Store, timestamp: Date.now() });
            return data as Store;
        }
        return null;
    },

    // Create store
    async create(store: StoreInsert): Promise<Store> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        const row: Record<string, unknown> = {
            ...store,
            template_id: store.template_id ?? 1,
            user_id: user.id,
        }
        const subRaw = store.subdomain
        if (subRaw == null || String(subRaw).trim() === '') {
            delete row.subdomain
        } else {
            row.subdomain = normalizeStoreSubdomainInput(String(subRaw))
        }

        const { data, error } = await db
            .from('stores')
            .insert(row)
            .select()
            .single()

        if (error) throw error
        return data
    },

    // Update store (production-safe: requires storeId)
    async update(storeId: string, updates: StoreUpdate): Promise<Store> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; store: Store }>(`/api/app/stores/${encodeURIComponent(storeId)}`, {
                method: 'PATCH',
                json: { updates },
            })
            if (!j || typeof j !== 'object' || !(j as { store?: unknown }).store) {
                throw new Error('Invalid API response')
            }
            return (j as { store: Store }).store
        }

        // RLS enforces access (owner or teammate). Do not filter by `user_id` here:
        // store rows use `user_id` = owner only; members would match 0 rows and never persist.
        const { data, error } = await db
            .from('stores')
            .update(updates)
            .eq('id', storeId)
            .select()
            .single()

        if (error) throw error
        return data
    },

    // Publish store
    async publish(storeId: string): Promise<Store> {
        return this.update(storeId, {
            is_published: true,
        })
    },

    // Unpublish store
    async unpublish(storeId: string): Promise<Store> {
        return this.update(storeId, { is_published: false })
    },


    // Toggle maintenance mode
    async toggleMaintenance(storeId: string, enabled: boolean): Promise<Store> {
        return this.update(storeId, { is_maintenance_mode: enabled })
    },

    // Check if slug is available
    // Rate limiter for slug/subdomain enumeration protection
    _slugCheckRateLimit: {
        count: 0,
        timestamp: Date.now()
    },

    // Check if slug is available
    async isSlugAvailable(slug: string): Promise<boolean> {
        // Enforce rate limit (max 10 checks per minute) to prevent enumeration bots
        const now = Date.now();
        if (now - this._slugCheckRateLimit.timestamp > 60000) {
            this._slugCheckRateLimit.count = 0;
            this._slugCheckRateLimit.timestamp = now;
        }
        if (this._slugCheckRateLimit.count >= 10) {
            console.warn('[Security] Rate limit exceeded for slug checks.');
            throw new Error('Trop de tentatives. Veuillez patienter une minute.');
        }
        this._slugCheckRateLimit.count++;

        const { data, error } = await db
            .from('stores')
            .select('id')
            .eq('slug', slug)
            .maybeSingle()
        if (error) throw error
        return data == null
    },

    // Check if subdomain is available
    async isSubdomainAvailable(subdomain: string): Promise<boolean> {
        // Enforce rate limit
        const now = Date.now();
        if (now - this._slugCheckRateLimit.timestamp > 60000) {
            this._slugCheckRateLimit.count = 0;
            this._slugCheckRateLimit.timestamp = now;
        }
        if (this._slugCheckRateLimit.count >= 10) {
            console.warn('[Security] Rate limit exceeded for subdomain checks.');
            throw new Error('Trop de tentatives. Veuillez patienter une minute.');
        }
        this._slugCheckRateLimit.count++;

        const normalized = normalizeStoreSubdomainInput(subdomain)
        if (!normalized || !isValidTenantSubdomainLabel(normalized) || isReservedTenantLabel(normalized)) {
            return false
        }

        const { data, error } = await db
            .from('stores')
            .select('id')
            .ilike('subdomain', normalized)
            .maybeSingle()
        if (error) throw error
        return data == null
    },

    // Update domain settings
    async updateDomainSettings(storeId: string, settings: { subdomain?: string; custom_domain?: string }): Promise<Store> {
        return this.update(storeId, settings)
    },

    // Increment visit count and track analytics
    async incrementVisits(_storeId: string): Promise<void> {
        // Visit increments previously used RPC; noop keeps storefront stable without widening client surface.
    },

    // Upload store logo
    async uploadLogo(file: File): Promise<string> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        const fileExt = file.name.split('.').pop()
        const fileName = `${Date.now()}.${fileExt}`
        const filePath = `${user.id}/${fileName}`

        const { error: uploadError } = await supabase.storage
            .from('brand-logos')
            .upload(filePath, file)

        if (uploadError) throw uploadError

        const { data: { publicUrl } } = supabase.storage
            .from('brand-logos')
            .getPublicUrl(filePath, {
                transform: {
                    width: 300,
                    quality: 80
                }
            })

        return publicUrl
    }
}
